/**
 * Which usable-looking response could not be used.
 *
 * A closed set rather than a message, for the same reason every other telemetry label is one: this
 * ships off-device, and a free-form string is how a token reaches a log. It is a discriminator with
 * three members because there are exactly three ways a 200 fails to be a session, and telling them
 * apart is the difference between "the Resource Server changed its contract" and "the Access Token
 * it minted is unreadable".
 */
export type MalformedResponse =
  /** `POST /Login` or `GET /Refresh` returned one of the two Resource Tokens, or neither. */
  | 'incompleteSession'
  /** The Access Token carried no readable `userId`, so nothing could be refreshed against later. */
  | 'unreadableCompanyUser'
  /** `GET /entitlements-token` answered with an empty token — an authorized session with no Scopes. */
  | 'emptyEntitlements';

/**
 * How the Authorization Server said no.
 *
 * The one cause here that clears the Vault, so which of the two it was is the first thing anyone
 * debugging a Forced Sign-out needs. The Dev1 pool binds refresh tokens to a device, so a missing
 * device key looks like a spent credential — which is why these are two members rather than the
 * bare word `refreshRejected` reaching Datadog for both.
 */
export type RefreshRejection =
  /** Amplify threw. Cognito refused the refresh token outright — expired, revoked, or unbound. */
  | 'authorizationServerRefused'
  /**
   * Amplify returned an empty session instead of throwing.
   *
   * Reached only after `hasSession()` found the whole required key set in the Vault, which is what
   * makes it a rejection rather than a bug — but it is a *different* rejection, and pointing the two
   * at the same dashboard row hides a device-binding failure behind an expiry.
   */
  | 'noIdToken';

/**
 * Which leg of a federated Grant failed.
 *
 * One cause with three details rather than three causes, because the machine routes on all of them
 * identically — the Grant is over, the Vault is untouched, and the person is back on the sign-in
 * screen with the same thing to try. What differs is only the diagnosis, which is exactly what
 * `detail` is for: two of these are deployment faults and one is ordinary, and a single row in
 * Datadog could not tell them apart.
 */
export type AuthorizationFailure =
  /**
   * The external user-agent could not be opened.
   *
   * Reachable on a device with no browser available to Custom Tabs, where federated sign-in cannot
   * work at all and a password is the only way in. It ends the Grant rather than escaping as a bug,
   * because the alternative is a spinner nobody can get out of.
   */
  | 'agentUnavailable'
  /**
   * The token endpoint refused the code or the verifier.
   *
   * A code is single-use and lives five minutes, so this is most often one already spent or expired.
   * It is also what an intercepted code looks like when the interceptor cannot produce the verifier
   * — which is PKCE working, not a failure of it.
   */
  | 'codeRejected'
  /**
   * This context cannot compute the S256 challenge, so the request was never sent.
   *
   * `crypto.subtle` exists only in a secure context. A packaged build is one — it is served from the
   * configured `hostname` — but Capacitor live reload sets `server.url`, which overrides that with a
   * plain-HTTP LAN origin, and there the digest throws. So federated sign-in cannot work under live
   * reload, and this is the cause that says so rather than a spinner nobody can get out of.
   *
   * Not worked around with `plain`: RFC 7636 §7.2 allows it only where SHA-256 is genuinely
   * unavailable, and it would put the verifier in the URL on an unclaimed scheme — see `pkce.ts`.
   */
  | 'pkceUnavailable'
  /**
   * The token endpoint answered 200 without the whole set of tokens.
   *
   * An app-client misconfiguration rather than anything a person did: no ID Token means the `openid`
   * scope is missing, and no refresh token means a session that cannot survive the next launch. The
   * second is why this fails the Grant instead of continuing with what did arrive — a session that
   * silently dies at every launch is worse than a Grant that fails where it can be reported.
   */
  | 'incompleteTokens';

/**
 * Which thing the Authorization Server asked for that this app has no way to collect.
 *
 * Cognito's `signInStep` union is far wider than what this app answers — an SMS code, a TOTP code, an
 * email code, and `DONE`. The rest each need a screen that does not exist. They are grouped into kinds
 * here rather than passed through, because Amplify's vocabulary stops at `AuthorizationServer` by
 * design and because what a dashboard needs is which *kind* of screen is missing, not which literal
 * Cognito emitted.
 *
 * `other` is what keeps the mapping total. Amplify has added three steps to that union in the
 * releases this app has been through, and a new one is by definition another thing we cannot
 * collect — so it must classify rather than fall out as an unclassified throw.
 */
export type UnsupportedStep =
  /** `CONFIRM_SIGN_UP`. The account was never confirmed, so there is nothing to sign in to yet. */
  | 'confirmSignUp'
  /**
   * `CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED`. The likeliest of the four to be met in the field:
   * it is what an administrator-created account gets at its first sign-in.
   */
  | 'newPasswordRequired'
  /** Cognito wants a factor chosen. Fires for any user with both SMS and TOTP enabled. */
  | 'mfaSelection'
  /** Cognito wants a factor enrolled — a TOTP secret, or an email address to send codes to. */
  | 'mfaSetup'
  /** Anything else, including a step added to Amplify's union after this was written. */
  | 'other';

/**
 * Why an auth operation failed.
 *
 * This is a union rather than a string because the machine has to *route* on the cause: a transient
 * network failure and a revoked token must not reach the same code path, and pattern-matching on
 * error text (`err?.name === 'NotAuthorizedException'`) cannot keep them apart.
 *
 * User-facing copy is deliberately absent. Mapping a cause to a message is a UI concern, so
 * the machine stays free of presentation and i18n.
 */
export type AuthFailureCause =
  /** Wrong email or password. Stay on the form. */
  | { readonly kind: 'invalidCredentials' }
  /**
   * A replayed sign-in was refused, which means the stored password is no longer the right one.
   *
   * Its own kind rather than {@link invalidCredentials}, because the two are different facts about
   * different people. A typed password that does not match is a typo, and "that email and password do
   * not match" is the right thing to say. The same refusal after a biometric prompt is a *stored* copy
   * gone stale — usually a password changed on another device — and telling someone who typed nothing
   * that their email and password do not match is both untrue and a description of the mechanism they
   * were never shown.
   *
   * Both are non-terminal and both end the Grant identically; only the copy and the one rule in
   * `failGrant` that forgets the stored copy read them apart.
   */
  | { readonly kind: 'storedCredentialsRejected' }
  /** The MFA code was wrong. Stay on the challenge. */
  | { readonly kind: 'challengeFailed' }
  /** The challenge was answered after Cognito's auth-flow session expired. */
  | { readonly kind: 'challengeExpired' }
  /**
   * The Authorization Server declined to answer, because too much has been asked of it too quickly.
   *
   * Raised on both the sign-in call and the Challenge answer, and its own cause rather than either of
   * their existing collapses because the advice inverts: "check what you typed and try again" and
   * "something went wrong, please try again" are both an instruction to do the thing that extends a
   * throttle.
   *
   * Where it leaves someone is decided per state rather than by {@link isRetryable} — see the challenge
   * arm of `transition()`.
   *
   * Email is what makes this likely rather than rare: someone waiting on slow mail has one recourse,
   * which is to abandon and sign in again — and that is the behaviour that produces the throttle.
   */
  | { readonly kind: 'rateLimited' }
  /**
   * The `state` nonce on an authorization response did not match, or the response arrived too
   * late to be plausible. A security event: log it, never retry it.
   */
  | { readonly kind: 'stateMismatch' }
  /** The Authorization Server rejected the Durable Root. The session is genuinely over. */
  | { readonly kind: 'refreshRejected'; readonly detail: RefreshRejection }
  /**
   * The Authorization Server accepted us but the Resource Server did not. Distinct from
   * `refreshRejected` because the Cognito session is still valid and must be torn down
   * explicitly, or the app will loop trying to reuse it.
   */
  | { readonly kind: 'resourceServerRejected'; readonly status: number }
  /** Offline, timeout, or 5xx. Retryable, and must never discard a session. */
  | { readonly kind: 'network' }
  /**
   * The OS reports no network path, so nothing was attempted.
   *
   * Its own cause rather than a `network` with a flag, because the two want opposite handling and
   * `isRetryable` is where that difference lives. `network` means a request went out and did not come
   * back — a 5xx, a timeout, a 429, an unreachable host — and any of those may work on the next try,
   * so it walks the ladder. This means there is no interface to try on, and four attempts over
   * fifteen seconds cannot change that. The person is told immediately instead.
   *
   * **Not a claim that the internet is unreachable**, only that the device says it has no path.
   * `Network.getStatus()` reports `connected: true` on a captive portal and on a dead VPN, so a
   * device that believes it is online still fails through `network` and still retries. This is the
   * clear case, not the only case.
   */
  | { readonly kind: 'deviceOffline' }
  /**
   * Both servers answered, and the answer is not a session the app can hold.
   *
   * Separate from `network` rather than folded into it, though both retry and neither ends a
   * session. Sharing `network` would make a 200 with an unusable body indistinguishable from being
   * offline, and tell people on the sign-in screen to check their connection after a request that
   * succeeded.
   */
  | { readonly kind: 'malformedResponse'; readonly detail: MalformedResponse }
  /** The Vault could not be read or written at the OS level. */
  | { readonly kind: 'vaultUnavailable' }
  /** The person cancelled the biometric prompt. */
  | { readonly kind: 'biometricCancelled' }
  /**
   * The biometric was offered and not accepted. Retryable by offering the prompt again.
   *
   * Its own cause rather than a `vaultUnavailable`, because those two route to opposite outcomes:
   * one is a device that cannot be used and clears the Vault, this one is a finger the sensor did
   * not recognise. Folding it into the other reports a rejected fingerprint as a *successful* read
   * over an empty keychain, which is the one signal that would say biometrics are failing in the
   * field saying the opposite.
   */
  | { readonly kind: 'biometricRejected' }
  /** Too many failed biometric attempts; the OS has locked biometrics. */
  | { readonly kind: 'biometricLockedOut' }
  /** The person abandoned an external user-agent without completing sign-in. */
  | { readonly kind: 'authorizationAbandoned' }
  /**
   * A federated Grant could not be completed, for a reason that is not the person's doing.
   *
   * Distinct from `authorizationAbandoned`, which is a choice and says nothing on screen. This one
   * has something to report — see {@link AuthorizationFailure} for which leg it was.
   */
  | { readonly kind: 'authorizationFailed'; readonly detail: AuthorizationFailure }
  /**
   * The Authorization Server asked for something this app cannot collect — see {@link UnsupportedStep}.
   *
   * A **successful** call to Cognito. The password was right and the Grant is progressing; what
   * stops it is that the next step needs a screen this app does not have. Reporting it as
   * `invalidCredentials` would tell someone their password was wrong when it was right, which is
   * what the bare `throw` it replaces was written to avoid — at the cost of escaping unclassified
   * and leaving them on a spinner instead.
   *
   * Not a security event. It is a pool-configuration mismatch, not an attack. It is worth watching,
   * though: it is the one path where a correct password reaches a screen that cannot be built without
   * a feature, and how often it fires decides whether that feature is worth building.
   */
  | { readonly kind: 'unsupportedChallenge'; readonly detail: UnsupportedStep }
  /**
   * A task could not be started because a Session Restore was running.
   *
   * The mirror of the guard `authorized` already applies in the other direction, where a background
   * refresh is dropped rather than allowed to cancel work someone asked for. `refreshing` is common
   * and short-lived — `preflightSkew` fires on any request with a near-expiry token, `appResumed` on
   * every foreground — so foregrounding the app and going straight to Settings is enough to meet it.
   *
   * Nothing failed and nothing is wrong: it says only "not now". The answer is to tap again a moment
   * later, which is what the copy says, and the reason it is a cause at all is that the alternative
   * was a tap with no visible consequence whatsoever.
   */
  | { readonly kind: 'sessionBusy' }
  /**
   * A Command handler threw without saying what its failure meant.
   *
   * The cause of last resort, and deliberately the vaguest one here. A handler classifies its own
   * failures because only it knows whether a rejection is a `grantFailed` or a `refreshFailed`, and
   * `classifyFailure` rethrows what it cannot recognise rather than guessing — a guessed cause could
   * end a session the error said nothing about. So the seam converts the escape into a
   * `commandFailed` event, and the state it lands in attaches this.
   *
   * Saying "it failed, and we cannot say why" is not inventing an outcome: the Command threw. What it
   * buys is that the wait ends. Before this, an unclassified throw left the state untouched, which on
   * `srpSignIn` or `biometricSignIn` is a spinner nobody can clear.
   *
   * It ends no session and clears no Vault — see {@link isTerminal}. A bug in a handler must not cost
   * anyone their Durable Root.
   */
  | { readonly kind: 'unexpectedFailure' };

/**
 * Whether this failure is safe to retry while keeping the current session.
 *
 * Exactly two causes are retryable, and they are the two where nobody refused us: the server could
 * not be reached, or it answered with something unusable. Everything else either needs the person
 * to act or means the session is genuinely over — retrying those would be a spin, not resilience.
 */
export function isRetryable(cause: AuthFailureCause): boolean {
  return cause.kind === 'network' || cause.kind === 'malformedResponse';
}

/**
 * Whether this failure ends the session.
 *
 * Note what is absent: `network`, `malformedResponse`, and every `biometric*` cause. A failure to
 * reach the network, a response nobody could parse, or a cancelled biometric prompt must not sign
 * anyone out.
 */
export function isTerminal(cause: AuthFailureCause): boolean {
  switch (cause.kind) {
    case 'refreshRejected':
    case 'resourceServerRejected':
    case 'stateMismatch':
    case 'vaultUnavailable':
      return true;
    case 'invalidCredentials':
    case 'storedCredentialsRejected':
    case 'challengeFailed':
    case 'challengeExpired':
    case 'network':
    case 'deviceOffline':
    case 'malformedResponse':
    case 'biometricCancelled':
    case 'biometricRejected':
    case 'biometricLockedOut':
    case 'authorizationAbandoned':
    case 'authorizationFailed':
      return false;
    // Ends a Grant, not a session — there was none. The credentials were accepted, so nothing here
    // justifies destroying a Durable Root this device may still be holding.
    case 'unsupportedChallenge':
      return false;
    // Nothing failed at all — a task was asked for at a moment one could not start.
    case 'sessionBusy':
      return false;
    // Nothing was refused about the credentials; the server declined to answer at all. Ending a
    // session here would let a rate limit cost someone their Durable Root.
    case 'rateLimited':
      return false;
    // Its own arm rather than one of the group above, because the reason is worth stating: a handler
    // that threw says nothing about the credentials, and ending a session over a bug is the outcome
    // this cause exists to avoid rather than one to route it to.
    case 'unexpectedFailure':
      return false;
  }
}
